<template>
  <div style="display: contents">
    <slot :definitions="definitions" :values="values" :on-input="onInput" />
  </div>
</template>
<script>
  // Renderless host for merchant custom-field inputs. Fetches definitions,
  // owns per-input values, normalizes numerical entries, and emits the
  // GraphQL-ready payload via v-model. The parent supplies the cell template
  // through the default scoped slot so it integrates with whatever grid
  // layout (Materialize row, list, modal, etc.) the host uses.
  //
  // Usage:
  //   <custom-fields v-model="customFields">
  //     <template #default="{ definitions, values, onInput }">
  //       <div class="input-field col s12 m6"
  //            v-for="(d, idx) in definitions" :key="'cf-' + d.label">
  //         <input
  //           :value="values[idx]"
  //           @input="onInput($event.target.value, idx, d)">
  //         <label>{{ d.label }}</label>
  //       </div>
  //     </template>
  //   </custom-fields>
  //
  // The root <div style="display: contents"> is layout-transparent, so the
  // slotted cells flow as direct children of the host's row.
  import gql from 'graphql-tag'

  // ACH forms natively capture First/Last Name, so they fetch the
  // backend-filtered list that omits definitions duplicating those inputs
  // (values are autofilled server-side from the native fields).
  const DEFINITIONS_QUERY = gql`query {
    merchant {
      merchantUser {
        settings {
          customFieldDefinitions { label type required }
        }
      }
    }
  }`
  const ACH_DEFINITIONS_QUERY = gql`query {
    merchant {
      merchantUser {
        settings {
          customAchFieldDefinitions { label type required }
        }
      }
    }
  }`

  export default {
    name: 'CustomFields',
    props: {
      flow: { type: String, default: null }
    },
    apollo: {
      definitions: {
        query() {
          return this.flow === 'ach' ? ACH_DEFINITIONS_QUERY : DEFINITIONS_QUERY
        },
        update(data) {
          const settings = data.merchant?.merchantUser?.settings || {}
          const defs = settings.customAchFieldDefinitions || settings.customFieldDefinitions || []
          // Pre-allocate slots so Vue 2 reactivity picks up writes by index.
          this.values = defs.map(() => '')
          this.emitPayload(defs, this.values)
          return defs
        }
      }
    },
    data() {
      return {
        definitions: [],
        values: []
      }
    },
    methods: {
      onInput(rawValue, idx, definition) {
        let next = (rawValue || '').toString()
        if (definition.type === 'numerical') {
          next = next.replace(/[^0-9.]/g, '')
          const firstDot = next.indexOf('.')
          if (firstDot !== -1) {
            next = next.slice(0, firstDot + 1) + next.slice(firstDot + 1).replace(/\./g, '')
          }
        }
        this.$set(this.values, idx, next)
        this.emitPayload(this.definitions, this.values)
      },
      emitPayload(defs, values) {
        const payload = defs
          .map((d, idx) => ({ label: d.label, value: (values[idx] || '').toString().trim() }))
          .filter(entry => entry.value.length > 0)
        this.$emit('input', payload)
      }
    }
  }
</script>
